<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { staticPageHref, accountHref, type AccountSection } from '@/router/products'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { useAuth } from '@/composables/useAuth'
import { useContracts } from '@/composables/useContracts'
import { useBank } from '@/composables/useBank'
import { useEcard } from '@/composables/useEcard'
import { downloadCertificate } from '@/services/account'
import { canDownloadCertificate } from '@/config/contractCertificate'
import { countryLabel } from '@/data/countries'
import AccountLayout from '@/components/layout/AccountLayout.vue'
import logoUrl from '@/assets/logo.webp'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { member } = useAuth()
const { contracts, hasFsmeCard } = useContracts()
const { bank, fullIban, fullBic, reveal } = useBank()
const { download: downloadEcardFile, downloading: ecardDownloading, errorCode: ecardErrorCode, errorMessage: ecardErrorMessage } = useEcard()

/* --- Téléchargement de l'attestation d'un contrat (/contracts/:id/generate_certificate). --- */
const downloadingRef = ref<string | null>(null)
const downloadError = ref<string | null>(null)

async function downloadAttestation(reference: string) {
  if (downloadingRef.value || typeof window === 'undefined') return
  downloadError.value = null
  downloadingRef.value = reference
  try {
    const blob = await downloadCertificate(reference)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `attestation-${reference}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch {
    downloadError.value = t('auth.account.contracts.actionError')
  } finally {
    downloadingRef.value = null
  }
}

useHead({ title: computed(() => `${t('auth.account.nav.dashboard')} — MySmerra`) })

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))

/** Accès réservé : sans session, retour à la page de connexion. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})

// Tuiles « Accès rapide » : `section` = sous-page de l'espace perso (route SPA),
// `href` = lien externe. Sinon placeholder « bientôt ».
// Sans carte de tiers payant (aucun contrat actif remonté à FSME), la tuile
// « Ma carte de tiers payant » cède sa place à « Tous les produits ».
const quickTiles = computed(() => [
  { key: 'contracts', section: 'contracts', href: null },
  hasFsmeCard.value
    ? { key: 'card', section: 'card', href: null }
    : { key: 'subscribe', section: 'subscribe', href: null },
  { key: 'documents', section: 'documents', href: null },
  { key: 'studlife', section: null, href: 'https://smerra.fr/studlife/' },
])

type QuickTile = (typeof quickTiles.value)[number]
/** URL cible d'une tuile (externe > sous-page). */
function tileHref(tile: QuickTile): string | undefined {
  if (tile.href) return tile.href
  if (tile.section) return accountHref(tile.section as AccountSection, currentLocale.value)
  return undefined
}
/** Clic tuile : route SPA pour une sous-page, défaut pour un lien externe,
 *  bloqué si placeholder. */
function onTileClick(tile: QuickTile, e: MouseEvent): void {
  if (tile.section) {
    e.preventDefault()
    router.push(accountHref(tile.section as AccountSection, currentLocale.value))
  } else if (!tile.href) {
    e.preventDefault()
  }
}

const birthDateLabel = computed(() => {
  const iso = member.value?.birthDate
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(locale.value, { day: '2-digit', month: '2-digit', year: 'numeric' })
})
const addressLabel = computed(() => {
  if (!member.value) return ''
  const a = member.value.address
  const line2 = a.line2 ? `, ${a.line2}` : ''
  const country = a.country ? `, ${countryLabel(a.country, currentLocale.value)}` : ''
  return `${a.line1}${line2}, ${a.postalCode} ${a.city}${country}`
})

// IBAN + BIC masqués par défaut ; révélation conjointe à la demande (lecture seule).
const showBank = ref(false)
async function toggleBank() {
  if (!showBank.value) await reveal()
  showBank.value = !showBank.value
}
const ibanLabel = computed(() => {
  if (!bank.value) return ''
  return showBank.value && fullIban.value ? fullIban.value : bank.value.maskedIban
})
const bicLabel = computed(() => {
  if (!bank.value) return ''
  return showBank.value && fullBic.value ? fullBic.value : bank.value.maskedBic
})

/** Libellé « bientôt » pour les actions non branchées (placeholders). */
const soon = computed(() => t('auth.account.nav.soon'))

/** Va à « Mes identifiants » avec le formulaire de changement de mot de passe ouvert. */
function goChangePassword() {
  router.push({ path: accountHref('credentials', currentLocale.value), query: { edit: 'password' } })
}

/** Va à « Mon RIB de remboursement » avec le formulaire ouvert (modification si un
 *  RIB existe, création sinon). */
function goEditRib() {
  router.push({ path: accountHref('payment', currentLocale.value), query: { edit: 'rib' } })
}

/** Va à « Mes informations » avec le formulaire d'édition ouvert. */
function goEditInfo() {
  router.push({ path: accountHref('info', currentLocale.value), query: { edit: 'info' } })
}
</script>

<template>
  <AccountLayout v-if="member" active="dashboard">
    <div class="dash">
      <!-- Accès rapide (raccourcis) : en tête, pleine largeur -->
      <section class="card qa-card" aria-labelledby="qa-title">
        <h2 id="qa-title" class="card__title">{{ t('auth.account.quickAccess.title') }}</h2>
        <div class="qa-grid">
          <a
            v-for="tile in quickTiles"
            :key="tile.key"
            class="qa-tile"
            :href="tileHref(tile)"
            :target="tile.href ? '_blank' : undefined"
            :rel="tile.href ? 'noopener' : undefined"
            :title="tileHref(tile) ? undefined : soon"
            @click="onTileClick(tile, $event)"
          >
            <span class="qa-tile__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <template v-if="tile.key === 'contracts'">
                  <path d="M7 3h7l4 4v14H7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                  <path d="M9 12h6M9 16h6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </template>
                <template v-else-if="tile.key === 'card'">
                  <rect x="3" y="6" width="18" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M3 10h18" fill="none" stroke="currentColor" stroke-width="1.6"/>
                </template>
                <template v-else-if="tile.key === 'subscribe'">
                  <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.6"/>
                  <path d="M17 14v6M14 17h6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </template>
                <template v-else-if="tile.key === 'documents'">
                  <path d="M4 5h6l2 2h8v12H4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                </template>
                <template v-else>
                  <path d="M12 3l2.5 5.5L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.5-.5z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                </template>
              </svg>
            </span>
            <span class="qa-tile__title">{{ t(`auth.account.quickAccess.${tile.key}.title`) }}</span>
            <span class="qa-tile__desc">{{ t(`auth.account.quickAccess.${tile.key}.desc`) }}</span>
            <span class="qa-tile__arrow" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="18" height="18">
                <path d="M4 10h11M10 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </a>
        </div>
      </section>

      <!-- Rangée principale : contrats (prioritaire) + carte de tiers payant -->
      <div class="dash__main">
        <!-- Mes contrats -->
        <section id="acc-contracts" class="card" aria-labelledby="ct-title">
          <h2 id="ct-title" class="card__title">{{ t('auth.account.contracts.title') }}</h2>
          <ul class="ct-list">
            <li v-for="c in contracts" :key="c.reference" class="ct-item">
              <div class="ct-item__info">
                <span class="ct-item__name">{{ c.name }}</span>
                <span class="ct-item__num">{{ t('auth.account.contracts.number') }} : {{ c.reference }}</span>
              </div>
              <span class="badge" :class="`badge--${c.status}`">{{ t(`auth.account.status.${c.status}`) }}</span>
              <!-- Terminated contracts stay listed, with their status badge, but
                   lose the attestation CTA (SI-1458). This dashboard downloads
                   inline instead of mounting the shared ContractCertificateAction,
                   so it cannot inherit that component's guard and applies the
                   rule here — calling the same predicate, not restating it. -->
              <button
                v-if="canDownloadCertificate(c)"
                type="button"
                class="btn-dark btn-dark--sm"
                :disabled="downloadingRef === c.reference"
                @click="downloadAttestation(c.reference)"
              >
                {{ downloadingRef === c.reference ? t('auth.account.contracts.downloading') : t('auth.account.contracts.download') }}
              </button>
            </li>
          </ul>
          <p v-if="downloadError" class="ct-error" role="alert">{{ downloadError }}</p>
          <RouterLink :to="accountHref('contracts', currentLocale)" class="ct-seeall">
            {{ t('auth.account.contracts.seeAll') }}
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M4 10h11M10 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </RouterLink>
        </section>

        <!-- Carte de tiers payant -->
        <section v-if="hasFsmeCard" id="acc-tpcard" class="card" aria-labelledby="tp-title">
          <h2 id="tp-title" class="card__title">{{ t('auth.account.tpCard.title') }}</h2>
          <div class="tp">
            <div class="tp__card">
              <div class="tp__card-head">
                <img class="tp__card-logo" :src="logoUrl" alt="SMERRA" width="462" height="122" />
                <span class="tp__card-title">{{ t('auth.account.tpCard.title') }}</span>
              </div>
              <dl class="tp__card-info">
                <div class="tp__card-group">
                  <dt>{{ t('auth.account.info.memberNumber') }}</dt>
                  <dd class="tp__card-value tp__card-value--accent">{{ member.memberNumber }}</dd>
                </div>
                <div class="tp__card-group">
                  <dt>{{ t('auth.account.tpCard.beneficiary') }}</dt>
                  <dd class="tp__card-value">{{ member.firstName }} {{ member.lastName }}</dd>
                </div>
                <div class="tp__card-group">
                  <dt>{{ member.civility === 'mrs' ? t('auth.account.tpCard.bornFemale') : t('auth.account.tpCard.bornMale') }}</dt>
                  <dd class="tp__card-value">{{ birthDateLabel }}</dd>
                </div>
              </dl>
              <p class="tp__card-disclaimer">{{ t('auth.account.tpCard.disclaimer') }}</p>
            </div>
            <button type="button" class="btn-dark tp__dl" :disabled="ecardDownloading" @click="downloadEcardFile('pdf')">{{ ecardDownloading ? t('auth.account.tpCard.downloading') : t('auth.account.tpCard.download') }}</button>
            <p v-if="ecardErrorCode" class="tp__dl-error" role="alert">{{ ecardErrorMessage }}</p>
          </div>
        </section>

        <!-- Sans carte de tiers payant : orientation vers le catalogue produits -->
        <section v-else id="acc-needs" class="card" aria-labelledby="needs-title">
          <h2 id="needs-title" class="card__title">{{ t('auth.account.needs.title') }}</h2>
          <p class="needs__desc">{{ t('auth.account.needs.desc') }}</p>
          <RouterLink class="btn-dark needs__cta" :to="accountHref('subscribe', currentLocale)">
            {{ t('auth.account.needs.cta') }}
          </RouterLink>
        </section>
      </div>

      <div class="dash__bottom">
        <!-- Mes informations personnelles -->
        <section class="card" aria-labelledby="info-title">
          <h2 id="info-title" class="card__title">{{ t('auth.account.info.title') }}</h2>
          <dl class="kv">
            <div class="kv__row"><dt>{{ t('auth.account.info.lastName') }}</dt><dd>{{ member.lastName }}</dd></div>
            <div class="kv__row"><dt>{{ t('auth.account.info.firstName') }}</dt><dd>{{ member.firstName }}</dd></div>
            <div class="kv__row"><dt>{{ t('auth.account.info.birthDate') }}</dt><dd>{{ birthDateLabel }}</dd></div>
            <div class="kv__row"><dt>{{ t('auth.account.info.nir') }}</dt><dd>{{ member.nir }}</dd></div>
            <div class="kv__row"><dt>{{ t('auth.account.info.memberNumber') }}</dt><dd>{{ member.memberNumber }}</dd></div>
            <div class="kv__row"><dt>{{ t('auth.account.info.phone') }}</dt><dd>{{ member.phone }}</dd></div>
            <div class="kv__row"><dt>{{ t('auth.account.info.address') }}</dt><dd>{{ addressLabel }}</dd></div>
          </dl>
          <button type="button" class="link-edit" @click="goEditInfo">{{ t('auth.account.info.edit') }}</button>
        </section>

        <!-- Mes identifiants -->
        <section class="card" aria-labelledby="cred-title">
          <h2 id="cred-title" class="card__title">{{ t('auth.account.credentials.title') }}</h2>
          <div class="field">
            <span class="field__label">{{ t('auth.account.credentials.email') }}</span>
            <span class="field__value">{{ member.email }}</span>
          </div>
          <div class="field">
            <span class="field__label">{{ t('auth.account.credentials.password') }}</span>
            <span class="field__value">••••••••</span>
            <button type="button" class="link-edit" @click="goChangePassword">{{ t('auth.account.credentials.editPassword') }}</button>
          </div>
          <p class="card__note">{{ t('auth.account.credentials.advice') }}</p>
        </section>

        <!-- Mon RIB de remboursement — réservé aux adhérents éligibles FSME
             (page dédiée également conditionnée, cf. PaymentView). -->
        <section v-if="hasFsmeCard" class="card" aria-labelledby="bank-title">
          <h2 id="bank-title" class="card__title">{{ t('auth.account.bank.title') }}</h2>
          <p class="card__note">{{ t('auth.account.bank.intro') }}</p>
          <template v-if="bank">
            <div class="bank">
              <div class="bank__text">
                <span class="bank__holder">{{ bank.holder }}</span>
                <span class="bank__iban">{{ ibanLabel }}</span>
                <span class="bank__bic">{{ bicLabel }}</span>
              </div>
              <button
                type="button"
                class="bank__reveal"
                :aria-label="showBank ? t('auth.account.bank.hideDetails') : t('auth.account.bank.showDetails')"
                :aria-pressed="showBank"
                @click="toggleBank"
              >
                <svg v-if="showBank" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                  <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7c2 0 3.8.66 5.3 1.6M22 12s-3.5 7-10 7c-2 0-3.8-.66-5.3-1.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4 4l16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <button type="button" class="link-edit" @click="goEditRib">{{ t('auth.account.bank.edit') }}</button>
          </template>
          <template v-else>
            <p class="card__note">{{ t('auth.account.bank.empty') }}</p>
            <!-- Éligible FSME sans RIB : proposer l'enregistrement du premier RIB
                 (deep-link ?edit=rib → formulaire de création ouvert). -->
            <button type="button" class="link-edit" @click="goEditRib">{{ t('auth.account.bank.create') }}</button>
          </template>
        </section>
      </div>
    </div>
  </AccountLayout>
</template>

<style scoped>
.dash { display: flex; flex-direction: column; gap: 1.25rem; }
.dash__main {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 1.25rem;
  align-items: start;
}
/* Personal info carries seven label/value rows and a long address: it takes the
 * wide column and spans the full height, while the two shorter account cards
 * stack beside it. Three equal columns squeezed every value onto two lines. */
.dash__bottom {
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  gap: 1.25rem;
  align-items: start;
}
.dash__bottom > :first-child { grid-column: 1; grid-row: span 2; }

/* Carte générique */
.card {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.card__title { font-size: var(--text-lg); font-weight: 700; color: var(--color-chocolate); }
.card__note { font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }

/* Accès rapide */
.qa-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}
@media (min-width: 40rem) {
  .qa-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 64rem) {
  .qa-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
.qa-tile {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 0.55rem;
  row-gap: 0.25rem;
  align-items: center;
  padding: 0.9rem;
  padding-bottom: 2.4rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--color-white);
  text-decoration: none;
  transition: border-color var(--dur-fast), box-shadow var(--dur-fast), transform var(--dur-base) var(--ease-spring);
}
.qa-tile:hover { border-color: var(--primary); box-shadow: var(--shadow-sm); transform: translateY(-1px); }
.qa-tile__icon { grid-column: 1; grid-row: 1; color: var(--primary); }
.qa-tile__title { grid-column: 2; grid-row: 1; font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.qa-tile__desc { grid-column: 2; grid-row: 2; font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }
.qa-tile__arrow {
  position: absolute;
  left: 0.9rem;
  bottom: 0.8rem;
  color: var(--color-chocolate);
}

/* Carte tiers payant (faux visuel) — visuel puis CTA centré dessous */
.tp { display: flex; flex-direction: column; gap: 0.9rem; }
.tp__card {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 0.9rem 1rem;
  border-radius: var(--radius-lg);
  color: var(--color-chocolate);
  background: url('/account/carte-tiers-payant.webp') right center / cover no-repeat,
    color-mix(in oklab, var(--color-peach) 60%, white);
  box-shadow: var(--shadow-sm);
}
.tp__card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem; }
.tp__card-logo { height: 1.5rem; width: auto; }
.tp__card-title {
  font-weight: 800;
  font-size: var(--text-xs);
  line-height: 1.15;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-cherry);
  text-align: right;
}
.tp__card-info { display: flex; flex-direction: column; gap: 0.5rem; margin: 0; }
.tp__card-group { display: flex; flex-direction: column; }
.tp__card-group dt { font-size: 0.7rem; color: var(--color-chocolate); }
.tp__card-value { font-size: var(--text-sm); font-weight: 700; line-height: 1.2; }
.tp__card-value--accent { color: var(--color-cherry); }
.tp__card-disclaimer { font-size: 0.6rem; color: var(--color-chocolate); line-height: 1.3; }
.tp .tp__dl { align-self: center; }
.tp__dl-error { margin-top: 0.4rem; text-align: center; font-size: var(--text-xs); color: var(--color-cherry); }

/* Encart « De quoi as-tu besoin ? » (à la place de la carte de tiers payant) */
.needs__desc { font-size: var(--text-sm); color: var(--text-muted); line-height: var(--leading-normal); }
.needs__cta { text-decoration: none; }

/* Liste contrats */
.ct-list { display: flex; flex-direction: column; }
.ct-item {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: 0.75rem;
  row-gap: 0.6rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
}
.ct-item:last-child { border-bottom: 0; }
.ct-item__info { grid-column: 1; grid-row: 1; display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
.ct-item > .badge { grid-column: 2; grid-row: 1; }
/* CTA sous les données du contrat (pas de place pour 2 colonnes). */
.ct-item > .btn-dark { grid-column: 1 / -1; grid-row: 2; justify-self: start; }
.ct-item__name { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); }
.ct-item__num { font-size: var(--text-xs); color: var(--text-muted); }
.ct-error { font-size: var(--text-sm); color: var(--color-cherry); }
.ct-seeall {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  align-self: flex-start;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
}
.ct-seeall:hover { color: var(--color-cherry); }

.badge {
  font-size: var(--text-xs);
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: var(--radius-pill);
  white-space: nowrap;
}
.badge--active { color: #1a7f45; background: #e6f6ec; }
.badge--pending { color: var(--color-chocolate); background: color-mix(in oklab, var(--color-peach) 35%, white); }
.badge--terminating,
.badge--retracting { color: var(--color-chocolate); background: color-mix(in oklab, var(--color-peach) 35%, white); }
.badge--terminated { color: var(--color-cherry); background: color-mix(in oklab, var(--color-cherry) 14%, white); }

/* Clé / valeur (infos perso) — libellé puis valeur, alignés à gauche */
.kv { display: flex; flex-direction: column; gap: 0.5rem; }
.kv__row {
  display: grid;
  grid-template-columns: 8.5rem 1fr;
  align-items: baseline;
  column-gap: 0.5rem;
}
.kv__row dt { font-size: var(--text-xs); font-weight: 600; color: var(--text-muted); }
.kv__row dt::after { content: " :"; }
.kv__row dd { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); }

/* Champs identifiants */
.field {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.1rem 0.75rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--border);
}
.field__label { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.field__value { font-size: var(--text-sm); font-weight: 600; color: var(--color-chocolate); grid-column: 1; }
.link-edit {
  justify-self: start;
  padding: 0;
  border: 0;
  background: none;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.18em;
  cursor: pointer;
}
.link-edit:hover { color: var(--color-cherry); }

/* Coordonnées bancaires — encart inséré, œil à droite */
.bank {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}
.bank__text { display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
.bank__holder { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.bank__iban { font-size: var(--text-sm); color: var(--color-chocolate); letter-spacing: 0.02em; word-break: break-all; }
.bank__bic { font-size: var(--text-sm); color: var(--color-chocolate); letter-spacing: 0.02em; word-break: break-all; }
.bank__reveal {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  padding: 0.2rem;
  border: 0;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
}
.bank__reveal:hover { color: var(--color-chocolate); }

/* Boutons sombres (placeholders) */
.btn-dark {
  align-self: flex-start;
  padding: 0.7rem 1.3rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);
}
.btn-dark:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }
.btn-dark--sm { padding: 0.5rem 1rem; font-size: var(--text-xs); }
.btn-dark--block { align-self: stretch; text-align: center; }

@media (max-width: 64rem) {
  .dash__main { grid-template-columns: 1fr; }
  /* Below the two-column layout every card is full width and flows in order. */
  .dash__bottom { grid-template-columns: 1fr; }
  .dash__bottom > :first-child { grid-column: auto; grid-row: auto; }
}
@media (min-width: 48rem) and (max-width: 64rem) {
  .dash__bottom { grid-template-columns: repeat(2, 1fr); }
}
</style>
